import React, { useCallback, useEffect, useState, useMemo, useRef } from 'react';
import { Editor, Frame, Element, useEditor, useNode } from '@craftjs/core';
import { useTheme } from '../../contexts/ThemeContext';
import { Toolbar } from './Toolbar';
import { SettingsPanel } from './SettingsPanel';
import {
  TextBlock,
  HeadingBlock,
  ImageBlock,
  VideoBlock,
  DividerBlock,
  PricingBlock,
  ContainerBlock,
  ColumnsBlock,
  Column,
  Canvas
} from './blocks';
import { Eye, Edit3, Save, Loader2 } from 'lucide-react';

// The editor content component
const EditorContent = ({ onSave, saving, initialContent }) => {
  const { isDark } = useTheme();
  const [isPreview, setIsPreview] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const loadedContentRef = useRef(null);
  const { actions, query, enabled } = useEditor((state) => ({
    enabled: state.options.enabled
  }));

  // Load initial content after mount - only load if content changed
  useEffect(() => {
    // Skip if no content or if we already loaded this exact content
    if (!initialContent) return;
    if (loadedContentRef.current === initialContent) return;
    
    try {
      const parsed = typeof initialContent === 'string' 
        ? JSON.parse(initialContent) 
        : initialContent;
      
      // Only deserialize if we have valid content with nodes
      if (parsed && parsed.ROOT) {
        // Use requestAnimationFrame for better timing with Craft.js
        requestAnimationFrame(() => {
          try {
            actions.deserialize(parsed);
            loadedContentRef.current = initialContent;
            setIsLoaded(true);
          } catch (deserializeErr) {
            console.error('Failed to deserialize content:', deserializeErr);
          }
        });
      }
    } catch (err) {
      console.error('Failed to parse content:', err);
    }
  }, [initialContent, actions]);

  // Toggle preview mode
  const togglePreview = () => {
    actions.setOptions(options => options.enabled = isPreview);
    setIsPreview(!isPreview);
  };

  // Handle save
  const handleSave = () => {
    const json = query.serialize();
    onSave(json);
  };

  return (
    <div className="flex flex-col h-full">
      {/* Top Actions Bar */}
      <div className={`flex items-center justify-between p-4 border-b ${isDark ? 'border-zinc-800' : 'border-gray-200'}`}>
        <div className="flex items-center gap-2">
          <button
            onClick={togglePreview}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg border ${
              isPreview
                ? 'bg-[#A2FF00] text-black border-[#A2FF00]'
                : isDark ? 'bg-zinc-800 border-zinc-700 text-white hover:bg-zinc-700' : 'bg-white border-gray-300 hover:bg-gray-50'
            }`}
          >
            {isPreview ? (
              <>
                <Edit3 className="h-4 w-4" />
                Edit
              </>
            ) : (
              <>
                <Eye className="h-4 w-4" />
                Preview
              </>
            )}
          </button>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#A2FF00] text-black font-medium hover:bg-[#8FE600] disabled:opacity-50"
        >
          {saving ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="h-4 w-4" />
              Save
            </>
          )}
        </button>
      </div>

      {/* Main Editor Area */}
      <div className="flex flex-1 overflow-hidden">
        {/* Editor Content */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {!isPreview && <Toolbar />}
          <div className={`flex-1 overflow-y-auto p-6 ${isDark ? 'bg-zinc-950' : 'bg-gray-100'}`}>
            <Frame>
              <Element
                canvas
                is={Canvas}
              >
                {/* Default content if empty */}
              </Element>
            </Frame>
          </div>
        </div>

        {/* Settings Panel */}
        {!isPreview && <SettingsPanel />}
      </div>
    </div>
  );
};

// Main ProposalBuilder export
export const ProposalBuilder = ({ initialContent, onSave, saving = false }) => {
  // Memoize resolver to prevent recreation on every render
  // IMPORTANT: Keys must match the displayName in each component's .craft config
  const resolver = useMemo(() => ({
    Canvas: Canvas,
    Text: TextBlock,
    Heading: HeadingBlock,
    Image: ImageBlock,
    Video: VideoBlock,
    Divider: DividerBlock,
    'Pricing Table': PricingBlock,
    Section: ContainerBlock,
    Columns: ColumnsBlock,
    Column: Column
  }), []);

  return (
    <Editor
      resolver={resolver}
      enabled={true}
      onRender={({ render }) => render}
    >
      <EditorContent 
        initialContent={initialContent}
        onSave={onSave}
        saving={saving}
      />
    </Editor>
  );
};

export default ProposalBuilder;
